import { useMemo } from 'react';

import { Route, Switch, useLocation } from 'react-router-dom';

import { FormGrid } from 'components';
import { Navbar } from 'components/Navbar';
import { PrivateRoute } from 'components/PrivateRoute';
import { TimeoutController } from 'components/TimeoutModal/TimeoutController';
import { ReviewedCandidateProvider } from 'contexts';
import { getReturnButtonPath } from 'helpers';
import { useUserContext } from 'hooks/useUserContext';
import { AdminCandidateTable } from 'pages/dashboard/AdminCandidateTable';
import { AdminOrganizationTable } from 'pages/dashboard/AdminOrganizationTable';
import { AdminTrancheTable } from 'pages/dashboard/AdminTrancheTable';
import { AdminUserTable } from 'pages/dashboard/AdminUserTable';
import { NotFound } from 'pages/error/NotFound';
import { PermissionDenied } from 'pages/error/PermissionDenied';
import { MetricsPage } from 'pages/metrics/MetricsPage';
import { OrganizationEditPage } from 'pages/organization_edit';
import { ReviewedCandidatePage } from 'pages/reviewed_candidate/ReviewedCandidatePage';
import { UserPage } from 'pages/user/UserPage';
import { UserProps } from 'types';

const createAuthChecks = (user: UserProps | null) => [
  {
    check: () => user?.role === 'candidate_admin',
    path: '/home/candidate_admin_dashboard',
  },
];

export const ContentWrapper = () => {
  const location = useLocation<{ from?: string }>();
  const { user, loaded } = useUserContext();
  const authChecks = useMemo(() => createAuthChecks(user), [user]);
  // This stops the auth checks in each route from firing before the user data is loaded
  if (!user && !loaded) {
    // Still loading
    return null;
  }
  return (
    <ReviewedCandidateProvider>
      <Navbar
        profile={false}
        links={[
          {
            path: '/home/candidate_admin_dashboard/reviewed_candidates',
            label: 'Candidates',
          },
          {
            path: '/home/candidate_admin_dashboard/organizations',
            label: 'Employers',
          },
          {
            path: '/home/candidate_admin_dashboard/tranches',
            label: 'Tranches',
          },
          {
            path: '/home/candidate_admin_dashboard/users',
            label: 'Users',
          },
          {
            path: '/home/candidate_admin_dashboard/metrics',
            label: 'Metrics',
          },
        ]}
      />
      <Switch>
        <PrivateRoute
          path='/home/candidate_admin_dashboard/reviewed_candidates/:candidate_id'
          component={() => (
            <FormGrid
              path={getReturnButtonPath(
                '/home/candidate_admin_dashboard/reviewed_candidates',
                location.state?.from,
              )}
              label='RETURN TO CANDIDATES'
              maxWidth='lg'
            >
              <ReviewedCandidatePage />
            </FormGrid>
          )}
          // exact
          authChecks={[
            {
              check: () => {
                return user?.role === 'candidate_admin';
              },
              path: '/home',
            },
          ]}
        />
        <PrivateRoute
          path='/home/candidate_admin_dashboard/reviewed_candidates'
          component={AdminCandidateTable}
          authChecks={authChecks}
        />
        <PrivateRoute
          path='/home/candidate_admin_dashboard/users/new_user'
          component={() => (
            <FormGrid
              path={getReturnButtonPath(
                '/home/candidate_admin_dashboard/users',
                location.state?.from,
              )}
              label='RETURN TO USERS'
              maxWidth='md'
            />
          )}
          authChecks={authChecks}
        />
        <PrivateRoute
          path='/home/candidate_admin_dashboard/users/:user_id'
          component={() => (
            <FormGrid
              path={getReturnButtonPath(
                '/home/candidate_admin_dashboard/users',
                location.state?.from,
              )}
              label='RETURN TO USERS'
            >
              <UserPage />
            </FormGrid>
          )}
          authChecks={authChecks}
        />

        <PrivateRoute
          path='/home/candidate_admin_dashboard/users'
          component={AdminUserTable}
          authChecks={authChecks}
        />
        <PrivateRoute
          path='/home/candidate_admin_dashboard/tranches'
          component={AdminTrancheTable}
          authChecks={authChecks}
        />
        <PrivateRoute
          path='/home/candidate_admin_dashboard/metrics'
          component={() => (
            <FormGrid
              path={getReturnButtonPath(
                '/home/candidate_admin_dashboard/reviewed_candidates',
                location.state?.from,
              )}
              label='RETURN TO CANDIDATES'
            >
              <MetricsPage />
            </FormGrid>
          )}
          authChecks={authChecks}
        />
        <PrivateRoute
          path='/home/candidate_admin_dashboard/organizations/:org_id'
          component={() => (
            <FormGrid
              path={getReturnButtonPath(
                '/home/candidate_admin_dashboard/organizations',
                location.state?.from,
              )}
              label='RETURN TO EMPLOYERS'
            >
              <OrganizationEditPage />
            </FormGrid>
          )}
          authChecks={authChecks}
        />
        <PrivateRoute
          path='/home/candidate_admin_dashboard/organizations'
          component={AdminOrganizationTable}
          authChecks={authChecks}
        />
        <Route
          path='/home/candidate_admin_dashboard/403'
          component={PermissionDenied}
        />
        <Route component={NotFound} />
      </Switch>
      <TimeoutController minutes={10} />
    </ReviewedCandidateProvider>
  );
};
